"use client";

import { useEffect } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useParams, usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import { FormButton } from "@/components/form/FormButton";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { ThemeSwitcher } from "@/components/sidebar/theme-switcher/ThemeSwitcher";
import { useStore } from "@/store";
import { useDirectChats } from "@/store/useDirectChats";
import { useTeamChats } from "@/store/useTeamChats";
import { twMerge } from "@/utils/twMerge";

import { MobileDocs } from "../mobile-docs/MobileDocs";
import { MobileDownloadButton } from "../mobile-download/MobileDownloadButton";
import { MobileMenuButton } from "../mobile-menu/MobileMenuButton";
import { MobileShareButton } from "../mobile-share/MobileShareButton";

const LoggedInContent = observer(function LoggedInContent({
  onTitleClick,
}: {
  onTitleClick: () => void;
}) {
  const { documentTree } = useStore();
  const teamChats = useTeamChats();
  const directChats = useDirectChats();
  const { chatId } = useParams<{ [key: string]: string | undefined }>();

  const chat = chatId ? (teamChats.getChat(chatId) ?? directChats.getChat(chatId)) : undefined;
  const title = chat ? chat.displayTitle : documentTree.currentDoc?.title;

  return (
    <div className={twMerge("flex flex-row justify-between py-1 px-6")}>
      <Sidebar.Button
        className="-ml-1"
        onClick={onTitleClick}
        icon={<Icon name="NavArrowLeft" className="sm:w-[28px] sm:h-[28px]" />}
      >
        <div
          className={twMerge(
            "flex-1 mx-1.5 truncate text-gray-500 text-[14px] text-left tracking-tight",
          )}
        >
          {title}
        </div>
      </Sidebar.Button>

      <div className="flex flex-row -mr-2">
        <MobileShareButton />
        <MobileDownloadButton />
        <ThemeSwitcher />
        <MobileMenuButton />
      </div>
    </div>
  );
});

const LoggedOutContent = observer(function LoggedOutContent() {
  const t = useTranslations();
  const { signInFlow } = useStore();

  const handleGetStartedClick = () => {
    signInFlow.redirectToLogin();
  };

  return (
    <div className={twMerge("flex flex-row flex-1 justify-between items-center py-5 px-6")}>
      <div className="flex flex-row items-center">
        <Icon name="CorcaLogo" width={40} height={40} />
        <div className="xs:hidden ml-2 tracking-tight">
          <div className="text-[17px] leading-[20px] font-semibold">{t("loginPanel.title")}</div>
          <div className="text-[15px] leading-[18px] text-gray-500">{t("loginPanel.subtitle")}</div>
        </div>
      </div>
      <FormButton
        onClick={handleGetStartedClick}
        aria-label={t("auth.signup.getStarted")}
        variant="accent"
        className="rounded-8 h-[34px] font-medium whitespace-nowrap"
      >
        {t("auth.signup.getStarted")}
      </FormButton>
    </div>
  );
});

export const MobileHeader = observer(function MobileHeader() {
  const { auth, view } = useStore();
  const { isLoggedIn } = auth;
  const pathname = usePathname();

  const handleDocsClick = () => {
    runInAction(() => {
      view.isMobileDocsVisible = true;
      view.isMobileDownloadVisible = false;
    });
  };

  useEffect(() => {
    runInAction(() => {
      view.isMobileDocsVisible = false;
    });
  }, [pathname, view, isLoggedIn]);

  return (
    <>
      <div
        className={twMerge(
          "hidden sm:flex",
          "fixed z-100 top-0 left-0",
          "flex-col w-full mb-auto bg-document-bg",
        )}
      >
        {isLoggedIn ? <LoggedInContent onTitleClick={handleDocsClick} /> : <LoggedOutContent />}
        <div className="h-[1px] mx-6 border-b border-gray-200 dark:border-gray-800" />
      </div>
      {view.isMobileDocsVisible && <MobileDocs />}
    </>
  );
});
